import React, { useState, useEffect, useRef, useCallback } from "react";
import { Table, Tag, Select, Rate, Input, notification, Badge, Button, Tooltip, Empty, Popconfirm, Space, message } from "antd";
import { SyncOutlined, LinkOutlined, DeleteOutlined } from "@ant-design/icons";
import api from "../services/api";

const { Option } = Select;

// ── Helper FireDAC / JSON puro (o novo endpoint devolve JSON simples,
//    mas mantemos o helper compatível com o resto do sistema) ─────────
const extractRows = (data) => {
  if (!data) return [];
  if (Array.isArray(data)) return data;
  if (data.FDBS) {
    try {
      const tables = data.FDBS.Manager.TableList;
      if (tables?.length > 0 && tables[0].RowList) return tables[0].RowList.map((r) => r.Original);
    } catch (e) {}
  }
  if (data.Table && Array.isArray(data.Table)) return data.Table;
  return [];
};

const POLL_INTERVAL_MS = 30000; // polling — ver observação sobre WebSocket no retorno do chat

const CORES_PROGRAMA = {
  LIVELO: "#e8008b",
  ESFERA: "#8b0000",
  "AZUL FIDELIDADE": "#003087",
  "LATAM PASS": "#d81a36",
  SMILES: "#ff6600",
  INTER: "#ff6b00",
  "C6 BANK": "#000000",
  SANTANDER: "#ec0000",
  NUBANK: "#820ad1",
  XP: "#1a1a2e",
  BTG: "#1a1a2e",
  SICREDI: "#009900",
  SICOOB: "#003c1a",
  // Redes de hotel (categoria HOTEL)
  "MARRIOTT BONVOY": "#1c2b4a",
  "HILTON HONORS": "#134a8e",
  "IHG ONE REWARDS": "#a10a2b",
  "WORLD OF HYATT": "#5b2a86",
  "WYNDHAM REWARDS": "#003da5",
  "MELIÁ REWARDS": "#b8860b",
  HOTEL: "#6d4c41",
};

const corPrograma = (nome = "") => CORES_PROGRAMA[nome?.toUpperCase()] || "#607d8b";

const fmtData = (v) => {
  if (!v) return "—";
  const s = String(v).trim();
  const iso = s.match(/^(\d{4})-(\d{2})-(\d{2})/);
  if (iso) return `${iso[3]}/${iso[2]}/${iso[1]}`;
  return s;
};

// Extrai o cupom do campo regras (o backend grava como "[Cupom: AZUL20]").
const extrairCupom = (regras = "") => {
  const m = (regras || "").match(/\[Cupom:\s*([^\]]+)\]/i);
  return m ? m[1].trim() : null;
};

export default function Promocoes() {
  const [loading, setLoading] = useState(false);
  const [excluindo, setExcluindo] = useState(false);
  const [promocoes, setPromocoes] = useState([]);
  const [filtroPrograma, setFiltroPrograma] = useState(null);
  const [filtroStatus, setFiltroStatus] = useState("ATIVA");
  const [busca, setBusca] = useState("");
  const [selecionadas, setSelecionadas] = useState([]);
  const pollRef = useRef(null);

  const carregarPromocoes = useCallback(async () => {
    setLoading(true);
    try {
      const params = {};
      if (filtroPrograma) params.Programa = filtroPrograma;
      if (filtroStatus) params.Status = filtroStatus;
      const res = await api.get("/ServerPrincipal/PesquisaPromocoes", { params });
      setPromocoes(extractRows(res.data));
    } catch (e) {
      console.error("Erro ao carregar promoções:", e);
    } finally {
      setLoading(false);
    }
  }, [filtroPrograma, filtroStatus]);

  // Polling de novas promoções — substitui o WebSocket enquanto ele não
  // existir no backend (ver observação no chat sobre RAD Server + WS).
  const verificarNovas = useCallback(async () => {
    try {
      const res = await api.get("/ServerPrincipal/PesquisaNovasPromocoes");
      const novas = extractRows(res.data);
      if (novas.length > 0) {
        novas.forEach((p) => {
          const programa = p.prom_programa ?? p.PROM_PROGRAMA ?? "";
          const parceiro = p.prom_parceiro ?? p.PROM_PARCEIRO ?? "";
          const bonus = p.prom_bonus ?? p.PROM_BONUS ?? 0;
          notification.open({
            message: "Nova promoção detectada!",
            description: `${programa} → ${parceiro}: ${bonus}% de bônus`,
            placement: "topRight",
            icon: <Badge status="processing" />,
          });
        });
        carregarPromocoes();
      }
    } catch (e) {
      // silencioso: falha de polling não deve incomodar o usuário
    }
  }, [carregarPromocoes]);

  // Exclui uma promoção por vez -- é o que o endpoint aceita
  // (DeletarPromocoes espera um único IDPromocao no corpo). Pra
  // exclusão em lote, disparamos uma chamada por item selecionado.
  const excluirUma = async (id) => {
    await api.post("/ServerPrincipal/DeletarPromocoes", { IDPromocao: id });
  };

  const excluirPromocao = async (row) => {
    setExcluindo(true);
    try {
      await excluirUma(row.key);
      setPromocoes((prev) =>
        prev.filter((p) => (p.prom_id ?? p.PROM_ID) !== row.key)
      );
      message.success(`Promoção de ${row.programa} excluída.`);
    } catch (e) {
      message.error("Erro ao excluir a promoção. Tenta de novo.");
    } finally {
      setExcluindo(false);
    }
  };

  const excluirSelecionadas = async () => {
    setExcluindo(true);
    const ids = [...selecionadas];
    let falhas = 0;
    for (const id of ids) {
      try {
        await excluirUma(id);
      } catch (e) {
        falhas++;
      }
    }
    setPromocoes((prev) =>
      prev.filter((p) => !ids.includes(p.prom_id ?? p.PROM_ID))
    );
    setSelecionadas([]);
    setExcluindo(false);
    if (falhas > 0) {
      message.warning(`${ids.length - falhas} excluída(s), ${falhas} falharam.`);
    } else {
      message.success(`${ids.length} promoção(ões) excluída(s).`);
    }
  };

  useEffect(() => {
    carregarPromocoes();
  }, [carregarPromocoes]);

  useEffect(() => {
    pollRef.current = setInterval(verificarNovas, POLL_INTERVAL_MS);
    return () => clearInterval(pollRef.current);
  }, [verificarNovas]);

  const programas = [...new Set(promocoes.map((p) => p.prom_programa ?? p.PROM_PROGRAMA).filter(Boolean))];

  const linhas = promocoes
    .filter((p) => {
      const parceiro = (p.prom_parceiro ?? p.PROM_PARCEIRO ?? "").toLowerCase();
      return !busca || parceiro.includes(busca.toLowerCase());
    })
    .map((p, i) => ({
      key: p.prom_id ?? p.PROM_ID ?? i,
      programa: p.prom_programa ?? p.PROM_PROGRAMA ?? "",
      parceiro: p.prom_parceiro ?? p.PROM_PARCEIRO ?? "",
      categoria: p.prom_categoria ?? p.PROM_CATEGORIA ?? "",
      bonus: Number(p.prom_bonus ?? p.PROM_BONUS ?? 0),
      estrelas: Number(p.prom_estrelas ?? p.PROM_ESTRELAS ?? 1),
      classificacao: p.prom_classificacao ?? p.PROM_CLASSIFICACAO ?? "",
      dtInicial: p.prom_dtinicial ?? p.PROM_DTINICIAL ?? "",
      dtFinal: p.prom_dtfinal ?? p.PROM_DTFINAL ?? "",
      link: p.prom_link ?? p.PROM_LINK ?? "",
      regras: p.prom_regras ?? p.PROM_REGRAS ?? "",
      status: p.prom_status ?? p.PROM_STATUS ?? "",
      nova: Number(p.prom_nova ?? p.PROM_NOVA ?? 0) === 1,
    }));

  const columns = [
    {
      title: "Programa",
      dataIndex: "programa",
      key: "programa",
      render: (v, row) => (
        <span style={{ display: "flex", alignItems: "center", gap: 8 }}>
          <span
            style={{
              width: 10, height: 10, borderRadius: "50%",
              background: corPrograma(v), display: "inline-block", flexShrink: 0,
            }}
          />
          <b>{v}</b>
          {row.nova && <Tag color="red">NOVO</Tag>}
        </span>
      ),
      filters: programas.map((p) => ({ text: p, value: p })),
      onFilter: (value, record) => record.programa === value,
    },
    { title: "Parceiro", dataIndex: "parceiro", key: "parceiro" },
    { title: "Categoria", dataIndex: "categoria", key: "categoria" },
    {
      title: "Bônus / Desconto",
      dataIndex: "bonus",
      key: "bonus",
      sorter: (a, b) => a.bonus - b.bonus,
      defaultSortOrder: "descend",
      render: (v, row) => {
        const cat = (row.categoria || "").toUpperCase();
        const isDesconto = cat === "HOTEL" || cat.includes("DESCONTO");
        const cupom = extrairCupom(row.regras);
        if (v > 0) {
          if (isDesconto)
            return (
              <span>
                <b style={{ color: v >= 20 ? "#389e0d" : "#333" }}>{v}% off</b>
                {cupom && <Tag color="gold" style={{ marginLeft: 6 }}>{cupom}</Tag>}
              </span>
            );
          return <b style={{ color: v >= 80 ? "#389e0d" : "#333" }}>{v}%</b>;
        }
        // Categorias baseadas em preço (hotel só com preço, emissão de
        // passagem) não têm % -- em vez de mostrar "0%" (parece que não
        // tem nada), mostra o preço extraído do texto do post, se achou.
        const m = (row.regras || "").match(/^\[([^\]]+)\]/);
        return (
          <span>
            {m ? <span style={{ color: "#555" }}>{m[1]}</span> : <span style={{ color: "#999" }}>—</span>}
            {cupom && <Tag color="gold" style={{ marginLeft: 6 }}>{cupom}</Tag>}
          </span>
        );
      },
    },
    {
      title: "Classificação",
      dataIndex: "estrelas",
      key: "estrelas",
      render: (v, row) => (
        <Tooltip title={row.classificacao}>
          <Rate disabled value={v} count={5} style={{ fontSize: 14 }} />
        </Tooltip>
      ),
    },
    {
      title: "Vigência",
      key: "vigencia",
      render: (_, row) => `${fmtData(row.dtInicial)} — ${fmtData(row.dtFinal)}`,
    },
    {
      title: "Status",
      dataIndex: "status",
      key: "status",
      render: (v) => <Tag color={v === "ATIVA" ? "green" : "default"}>{v}</Tag>,
    },
    {
      title: "",
      key: "link",
      render: (_, row) =>
        row.link ? (
          <a href={row.link} target="_blank" rel="noopener noreferrer">
            <LinkOutlined /> Acessar
          </a>
        ) : null,
    },
    {
      title: "",
      key: "acoes",
      render: (_, row) => (
        <Popconfirm
          title="Excluir esta promoção?"
          description="Isso apaga o registro definitivamente."
          okText="Excluir"
          okButtonProps={{ danger: true }}
          cancelText="Cancelar"
          onConfirm={() => excluirPromocao(row)}
        >
          <Button danger type="text" icon={<DeleteOutlined />} loading={excluindo} />
        </Popconfirm>
      ),
    },
  ];

  return (
    <div className="radar-promocoes-container" style={{ padding: 24, background: "#f0f2f5", minHeight: "100vh" }}>
      <div className="radar-header" style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: 16, flexWrap: "wrap", gap: 12 }}>
        <div style={{ fontSize: 22, fontWeight: 700, color: "#1a1a2e" }}>Radar de Promoções</div>
        <Space wrap>
          {selecionadas.length > 0 && (
            <Popconfirm
              title={`Excluir ${selecionadas.length} promoção(ões)?`}
              description="Isso apaga os registros definitivamente."
              okText="Excluir"
              okButtonProps={{ danger: true }}
              cancelText="Cancelar"
              onConfirm={excluirSelecionadas}
            >
              <Button danger icon={<DeleteOutlined />} loading={excluindo}>
                Excluir selecionadas ({selecionadas.length})
              </Button>
            </Popconfirm>
          )}
          <Button icon={<SyncOutlined spin={loading} />} onClick={carregarPromocoes}>
            Atualizar
          </Button>
        </Space>
      </div>

      <div className="radar-filtros" style={{ display: "flex", gap: 12, marginBottom: 16, flexWrap: "wrap" }}>
        <Select
          placeholder="Programa"
          allowClear
          className="radar-filtro-item"
          style={{ width: 220 }}
          value={filtroPrograma}
          onChange={setFiltroPrograma}
        >
          {programas.map((p) => (
            <Option key={p} value={p}>{p}</Option>
          ))}
        </Select>

        <Select className="radar-filtro-item" style={{ width: 180 }} value={filtroStatus} onChange={setFiltroStatus}>
          <Option value="ATIVA">Ativas</Option>
          <Option value="ENCERRADA">Encerradas</Option>
          <Option value="">Todas (vigentes)</Option>
        </Select>

        <Input.Search
          placeholder="Buscar parceiro..."
          allowClear
          className="radar-filtro-item"
          style={{ width: 260 }}
          onChange={(e) => setBusca(e.target.value)}
        />
      </div>

      <div className="radar-table-scroll">
        <Table
          columns={columns}
          dataSource={linhas}
          loading={loading}
          pagination={{ pageSize: 15, showSizeChanger: true }}
          locale={{ emptyText: <Empty description="Nenhuma promoção encontrada" /> }}
          rowClassName={(row) => (row.nova ? "linha-promocao-nova" : "")}
          rowSelection={{
            selectedRowKeys: selecionadas,
            onChange: setSelecionadas,
          }}
          scroll={{ x: 900 }}
        />
      </div>

      <style>{`
        .linha-promocao-nova {
          background: #fffbe6;
        }

        /* Mobile: filtros empilham em vez de espremer numa linha só,
           e a tabela rola na horizontal em vez de quebrar layout. */
        @media (max-width: 640px) {
          .radar-promocoes-container {
            padding: 12px;
          }
          .radar-header {
            flex-direction: column;
            align-items: stretch;
          }
          .radar-header > div:first-child {
            font-size: 18px;
          }
          .radar-filtros {
            flex-direction: column;
          }
          .radar-filtro-item {
            width: 100% !important;
          }
          .radar-table-scroll {
            margin: 0 -12px;
            padding: 0 12px;
            overflow-x: auto;
            -webkit-overflow-scrolling: touch;
          }
        }
      `}</style>
    </div>
  );
}